import type { ReactNode } from 'react';

/**
 * The landing page's icon set.
 *
 * These were unicode glyphs, `◌ ↗ ◎ ✓ ⌕ ◇ ↻`, dropped into a span. A glyph
 * is drawn by whatever font happens to resolve, at whatever weight that font
 * gives it, on a baseline it chose: at 22px in a 40px tile they came out as
 * hairline fragments, each a different weight from the one beside it.
 *
 * Drawn instead, on one 24 grid, one stroke width, one set of caps and joins,
 * so four icons in a row read as four members of the same family.
 */

export type LandingIconName =
  | 'palette'
  | 'signal'
  | 'target'
  | 'shield'
  | 'search'
  | 'angles'
  | 'check'
  | 'loop'
  | 'cross'
  | 'lock'
  | 'spark'
  | 'heart'
  | 'arrow'
  | 'tag'
  | 'grid'
  | 'globe';

const PATHS: Record<LandingIconName, ReactNode> = {
  /*
   * A brand's visual language.
   *
   * This was a rectangle with four dots under it, which at 20px is a monitor
   * with a row of specks beneath it. The palette outline is unmistakable at
   * any size, and the paint wells are drawn as zero-length round-capped
   * strokes so they read as dots without breaking the set's `fill: none`.
   */
  palette: (
    <>
      <path d="M12 3.4a8.6 8.6 0 1 0 0 17.2c1.25 0 1.9-.78 1.9-1.7 0-.47-.2-.9-.5-1.2a1.7 1.7 0 0 1 1.2-2.9h1.36a4.64 4.64 0 0 0 4.64-4.64c0-3.9-3.85-6.76-8.6-6.76Z" />
      <path d="M8.1 9.2h.01M11.9 7.4h.01M15.7 9.4h.01M7.5 13.4h.01" />
    </>
  ),
  /* Buying signals: a reading that trends up. */
  signal: (
    <>
      <path d="M3.5 16.5 9 11l4 4 7.5-7.5" />
      <path d="M15 7.5h5.5V13" />
    </>
  ),
  /* The customer, known: a mark with something at its centre. */
  target: (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <circle cx="12" cy="12" r="3.4" />
    </>
  ),
  /* Claims that hold: a guard with a tick inside it. */
  shield: (
    <>
      <path d="M12 3.2 19 6v5.6c0 4.3-2.9 7.4-7 8.8-4.1-1.4-7-4.5-7-8.8V6l7-2.8Z" />
      <path d="m9 12 2.2 2.2L15.4 10" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.4" />
      <path d="m15.8 15.8 4 4" />
    </>
  ),
  /* Distinct angles: three directions from one origin. */
  angles: (
    <>
      <circle cx="5.5" cy="12" r="1.9" />
      <path d="M7.4 12h3.2M12.6 12h3.2" />
      <path d="m11.4 11 5.6-4.4M11.4 13l5.6 4.4" />
      <circle cx="18.6" cy="6.1" r="1.7" />
      <circle cx="18.6" cy="17.9" r="1.7" />
    </>
  ),
  check: <path d="m5 12.6 4.6 4.6L19 7.8" />,
  /* Feedback coming back around. */
  loop: (
    <>
      <path d="M20 12a8 8 0 1 1-2.4-5.7" />
      <path d="M20 4.2v5h-5" />
    </>
  ),
  cross: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />,
  /*
   * The button arrow, and the last glyph on this page that was not drawn.
   * `→` was set a few pixels larger than the word beside it so it would not
   * look spindly, which put its ink on a different centre from the text's:
   * the two were aligned as boxes and misaligned to the eye. Drawn on the
   * same 24 grid as everything else here, it sits where it is put. The same
   * path the bar's own arrow uses (`BlogNav.tsx`), so a reader moving between
   * them is looking at one arrow.
   */
  arrow: <path d="M4.5 12h14M13 6.5 18.5 12 13 17.5" />,
  /*
   * A price tag, for the discount pill. The sparkle that stood there is the
   * mark this page uses for what the machine made, which is not what an offer
   * is: a tag is the thing a shop hangs off a product, and it is the shape a
   * reader already reads as a price coming down.
   *
   * The hole is a zero-length round-capped stroke, the same way the palette's
   * paint wells are drawn, so it reads as a dot without breaking the set's
   * `fill: none`.
   */
  tag: (
    <>
      <path d="M20.4 12.6 11.9 4.1a1.7 1.7 0 0 0-1.2-.5H5a1.5 1.5 0 0 0-1.5 1.5v5.7c0 .4.2.9.5 1.2l8.5 8.5a1.7 1.7 0 0 0 2.4 0l5.5-5.5a1.7 1.7 0 0 0 0-2.4Z" />
      <path d="M7.6 7.6h.01" strokeWidth="2.4" />
    </>
  ),
  lock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2.5" />
      <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
    </>
  ),
  /* The mark on a claim worth reading twice. */
  spark: (
    <path d="M12 3.3c.9 4.6 3.6 7.3 8.2 8.2-4.6.9-7.3 3.6-8.2 8.2-.9-4.6-3.6-7.3-8.2-8.2 4.6-.9 7.3-3.6 8.2-8.2Z" />
  ),
  heart: (
    <path d="M12 20.2 4.9 13a4.55 4.55 0 0 1 6.44-6.43l.66.66.66-.66A4.55 4.55 0 0 1 19.1 13Z" />
  ),
  /* Ready-made sections: a page laid out in blocks. */
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.6" />
      <rect x="13" y="4" width="7" height="7" rx="1.6" />
      <rect x="4" y="13" width="7" height="7" rx="1.6" />
      <rect x="13" y="13" width="7" height="7" rx="1.6" />
    </>
  ),
  /* Many languages: a world, with its lines of latitude and a meridian. */
  globe: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M3.4 12h17.2M12 3.4c2.3 2.3 3.6 5.4 3.6 8.6s-1.3 6.3-3.6 8.6c-2.3-2.3-3.6-5.4-3.6-8.6S9.7 5.7 12 3.4Z" />
    </>
  ),
};

export function LandingIcon({ name }: { name: LandingIconName }) {
  return (
    <svg className="lp-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {PATHS[name]}
    </svg>
  );
}

/** WhatsApp's mark, filled in the text colour of the button it sits in. */
export function WhatsAppGlyph() {
  return (
    <svg className="lp-brand-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {/*
        One colour, the button's own: a green mark on a white disc inside a
        blue button was three colours in a 20px space and read as a sticker.
      */}
      <path
        fill="currentColor"
        d="M12 2.2a9.8 9.8 0 0 0-8.4 14.8L2.2 21.8l4.9-1.3A9.8 9.8 0 1 0 12 2.2Zm0 17.9a8.1 8.1 0 0 1-4.2-1.2l-.3-.2-2.9.8.8-2.8-.2-.3A8.1 8.1 0 1 1 12 20.1Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.6.8-.8 1-.3.2-.5.1a6.6 6.6 0 0 1-3.3-2.9c-.2-.4.3-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7c.1.2 1.9 2.9 4.6 4 1.7.7 2.4.8 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.5-.3Z"
      />
    </svg>
  );
}
